import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// ─────────────────────────────────────────────────────────────────────────────
// Pitstop dispatch schedules + queues — scheduled auto-dispatch v2
// (docs/pitstop/scheduled-dispatch-trd-v2.md §D3/D9, nest-logistic-service).
//
// v2 replaces the per-schedule "runs" model with per-upload "queues": one row
// per (pitstop, scheduledAt) slot, created when the first CSV upload targets
// that slot. Schedules are now a pure master of offered dispatch times.
//
// NOTE: these live under `/v1/dispatch/pitstops/*` on the LOGISTIC service —
// distinct from `/v1/pitstop` (singular), the core masterdata proxy used by
// `./pitstops.ts`. The prefix is registered in `@/config/logistic-api.ts`.
// ─────────────────────────────────────────────────────────────────────────────

/** Short ID labels for ISO weekdays — index 0 = ISO 1 (Senin) … 6 = 7 (Minggu). */
export const ISO_DAY_LABELS = [
  'Sen',
  'Sel',
  'Rab',
  'Kam',
  'Jum',
  'Sab',
  'Min',
] as const;

/** `[1,3]` → `Sen Rab`; all seven days → `Setiap hari`. */
export const formatActiveDays = (days: number[]): string =>
  days.length === 7
    ? 'Setiap hari'
    : days.map((d) => ISO_DAY_LABELS[d - 1] ?? String(d)).join(' ');

/** Lifecycle of one auto-dispatch queue slot (TRD v2 §D2). */
export type PitstopQueueStatus =
  | 'QUEUED'
  | 'PREVIEWED'
  | 'COMMITTED'
  | 'SKIPPED_EMPTY'
  | 'FAILED';

export interface PitstopSchedule {
  id: string;
  pitstopId: number;
  clientId: number;
  /** Wall-clock dispatch time, `HH:mm` in `timezone`. */
  dispatchTime: string;
  timezone: string;
  /** ISO weekdays the slot is offered on (1 = Senin … 7 = Minggu). */
  activeDays: number[];
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Compact latest-queue summary embedded on each list row (§D9). */
export interface PitstopScheduleLastQueue {
  scheduledAt: string;
  status: PitstopQueueStatus;
  dispatchId: string | null;
  dispatchCode: string | null;
  error: string | null;
  /** Full failure detail (stage + message + stack) for tracing. */
  errorDetail: string | null;
}

/** Row of `GET /v1/dispatch/pitstops/:pitstopId/schedules` — schedule + last queue. */
export interface PitstopScheduleListRow extends PitstopSchedule {
  lastQueue: PitstopScheduleLastQueue | null;
}

/** Row of `GET /v1/dispatch/pitstops/schedules` — the global board adds display names. */
export interface PitstopScheduleBoardRow extends PitstopScheduleListRow {
  pitstopName: string;
  clientName: string;
}

/**
 * One queue row — shared by the per-pitstop history
 * (`GET /v1/dispatch/pitstops/:pitstopId/queues`) and the global board
 * (`GET /v1/dispatch/pitstops/queues`).
 */
export interface PitstopQueue {
  id: string;
  pitstopId: number;
  clientId: number;
  /** Originating schedule; null if the schedule was deleted afterwards. */
  scheduleId: string | null;
  /** Instant T of the slot (ISO). The dispatch is built at upload time; the
   *  commit runs at T. */
  scheduledAt: string;
  status: PitstopQueueStatus;
  dispatchId: string | null;
  dispatchCode: string | null;
  error: string | null;
  previewedAt: string | null;
  committedAt: string | null;
  notifiedAt: string | null;
  createdAt: string;
}

/** Audit action on a schedule (`GET /v1/dispatch/pitstops/schedules/:id/changes`). */
export type PitstopScheduleChangeAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'ACTIVATE'
  | 'DEACTIVATE';

/** One "Riwayat perubahan" audit row (TRD v2 §D3). */
export interface PitstopScheduleChange {
  id: string;
  action: PitstopScheduleChangeAction;
  actorName: string;
  actorEmail: string | null;
  /** before→after diff; shape depends on the action. */
  payload: Record<string, unknown> | null;
  createdAt: string;
}

/** Why a slot's rider pool came out empty (§D7 lens). */
export type SlotRosterReason =
  | 'NO_ZONE_DRIVERS'
  | 'NO_CALENDAR_SLOT'
  | 'NOBODY_CONFIRMED';

export interface SlotRosterRider {
  id: number;
  code: string;
  name: string;
}

/**
 * `GET /v1/dispatch/pitstops/:pitstopId/slot-roster` — the riders that would
 * actually be dispatched for one (client, date, time) slot. Feeds the import
 * modal's "Rider bertugas" panel; the same pool hard-gates the upload
 * (IMPORT_SLOT_NO_RIDER).
 */
export interface SlotRoster {
  pitstopId: number;
  clientId: number;
  scheduledAt: string;
  riders: SlotRosterRider[];
  /** Null when `riders` is non-empty. */
  reason: SlotRosterReason | null;
}

export interface CreatePitstopSchedulePayload {
  /** `HH:mm` wall-clock. */
  dispatchTime: string;
  /** Optional; BE defaults to `Asia/Jakarta`. */
  timezone?: string;
  /** ISO weekdays (1=Sen … 7=Min); BE defaults to every day. */
  activeDays?: number[];
  notes?: string | null;
}

export interface UpdatePitstopSchedulePayload {
  dispatchTime?: string;
  activeDays?: number[];
  notes?: string | null;
  isActive?: boolean;
}

/**
 * Backend error codes surfaced by these endpoints:
 *   404 `PITSTOP_NOT_FOUND` / `SCHEDULE_NOT_FOUND`
 *   409 `PITSTOP_NOT_CLIENT` — pitstop is not a `PROVIDER_WAREHOUSE`
 *   409 `SCHEDULE_ALREADY_EXISTS` — duplicate (pitstop, dispatchTime)
 *   409 `SCHEDULE_IN_USE` — delete refused (a queue referenced it), deactivate
 *       instead
 */
export type PitstopScheduleErrorCode =
  | 'PITSTOP_NOT_FOUND'
  | 'SCHEDULE_NOT_FOUND'
  | 'PITSTOP_NOT_CLIENT'
  // Warehouse exists in core but carries no provider — can't be scheduled.
  | 'PITSTOP_NO_CLIENT'
  | 'SCHEDULE_ALREADY_EXISTS'
  | 'SCHEDULE_IN_USE'
  | 'UNKNOWN';

export class PitstopScheduleError extends Error {
  code: PitstopScheduleErrorCode;
  constructor(code: PitstopScheduleErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

const parseScheduleError = (error: any): PitstopScheduleError => {
  const data = error?.response?.data;
  const code = (data?.error as PitstopScheduleErrorCode) || 'UNKNOWN';
  const message =
    (data?.message as string) ||
    (typeof data?.error === 'string' ? data.error : null) ||
    getBackendErrorMessage(error);
  return new PitstopScheduleError(code, message);
};

// ─────────────────────────────────────────────────────────────────────────────
// Envelope helper — `{ status, message, data }` unwrap (mirror shipments)
// ─────────────────────────────────────────────────────────────────────────────

type Envelope<T> = { status: string | number; message?: string; data: T };

const unwrap = <T>(payload: Envelope<T> | T): T =>
  payload && typeof payload === 'object' && 'data' in (payload as any)
    ? (payload as Envelope<T>).data
    : (payload as T);

// The list endpoints wrap their rows one level deeper: `data: { data: [...] }`.
const unwrapList = <T>(payload: unknown): T[] => {
  const outer = unwrap<{ data?: T[] } | T[]>(payload as any);
  if (Array.isArray(outer)) return outer;
  return outer?.data ?? [];
};

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────

export const pitstopSchedulesAPI = {
  /** Schedules of one pitstop, each with its latest queue (§D9). */
  list: async (
    pitstopId: number | string
  ): Promise<PitstopScheduleListRow[]> => {
    try {
      const response = await Axios.get(`/v1/dispatch/pitstops/${pitstopId}/schedules`);
      return unwrapList<PitstopScheduleListRow>(response.data);
    } catch (error) {
      throw parseScheduleError(error);
    }
  },

  /** Create a schedule (201). Pure DB write in v2 — no scheduler side-effect. */
  /** Every slot across all client pitstops — the Jadwal Dispatch board. */
  listAll: async (): Promise<PitstopScheduleBoardRow[]> => {
    try {
      const res = await Axios.get<Envelope<unknown>>('/v1/dispatch/pitstops/schedules');
      return unwrapList<PitstopScheduleBoardRow>(res.data);
    } catch (error) {
      throw parseScheduleError(error);
    }
  },

  /**
   * Rider pool of one slot — who rides if the client uploads for
   * (`date`, `time`). `date`/`time` are the schedule's own wall-clock pair
   * (the same values the import payload sends).
   */
  slotRoster: async (
    pitstopId: number | string,
    params: { clientId: number; date: string; time: string }
  ): Promise<SlotRoster> => {
    try {
      const response = await Axios.get(
        `/v1/dispatch/pitstops/${pitstopId}/slot-roster`,
        { params }
      );
      return unwrap<SlotRoster>(response.data);
    } catch (error) {
      throw parseScheduleError(error);
    }
  },

  create: async (
    pitstopId: number | string,
    payload: CreatePitstopSchedulePayload
  ): Promise<PitstopSchedule> => {
    try {
      const response = await Axios.post(
        `/v1/dispatch/pitstops/${pitstopId}/schedules`,
        payload
      );
      return unwrap<PitstopSchedule>(response.data);
    } catch (error) {
      throw parseScheduleError(error);
    }
  },

  update: async (
    id: string,
    payload: UpdatePitstopSchedulePayload
  ): Promise<PitstopSchedule> => {
    try {
      const response = await Axios.patch(
        `/v1/dispatch/pitstops/schedules/${id}`,
        payload
      );
      return unwrap<PitstopSchedule>(response.data);
    } catch (error) {
      throw parseScheduleError(error);
    }
  },

  /**
   * Hard delete. Refused with 409 `SCHEDULE_IN_USE` once any queue referenced
   * the schedule — deactivate (`update(id, { isActive: false })`) instead.
   */
  remove: async (id: string): Promise<{ deleted: boolean }> => {
    try {
      const response = await Axios.delete(`/v1/dispatch/pitstops/schedules/${id}`);
      return unwrap<{ deleted: boolean }>(response.data);
    } catch (error) {
      throw parseScheduleError(error);
    }
  },

  /**
   * Queue history + upcoming slots of one pitstop (§D9). `from`/`to` are
   * inclusive `YYYY-MM-DD`; BE defaults to the last 14 days .. +7 days.
   */
  queues: async (
    pitstopId: number | string,
    params: { from?: string; to?: string } = {}
  ): Promise<PitstopQueue[]> => {
    try {
      const response = await Axios.get(`/v1/dispatch/pitstops/${pitstopId}/queues`, {
        params,
      });
      return unwrapList<PitstopQueue>(response.data);
    } catch (error) {
      throw parseScheduleError(error);
    }
  },

  /** Global queue board (`GET /v1/dispatch/pitstops/queues`) — the Jadwal Dispatch page. */
  queuesGlobal: async (
    params: {
      from?: string;
      to?: string;
      status?: PitstopQueueStatus;
      pitstopId?: number | string;
    } = {}
  ): Promise<PitstopQueue[]> => {
    try {
      const response = await Axios.get('/v1/dispatch/pitstops/queues', { params });
      return unwrapList<PitstopQueue>(response.data);
    } catch (error) {
      throw parseScheduleError(error);
    }
  },

  /** "Riwayat perubahan" — audit trail of one schedule (§D3). */
  changes: async (scheduleId: string): Promise<PitstopScheduleChange[]> => {
    try {
      const response = await Axios.get(
        `/v1/dispatch/pitstops/schedules/${scheduleId}/changes`
      );
      return unwrapList<PitstopScheduleChange>(response.data);
    } catch (error) {
      throw parseScheduleError(error);
    }
  },
};
